import type { TFunction } from 'i18next'

const TABS = 'cases.investigate.entityResolution.tabs'

export interface RailBadge {
  count: number
  /** Spoken instead of the bare number; undefined when the number says it all. */
  label?: string
}

/** What waits behind each tab of Entity Resolution; null for a tab the page does not offer. */
export interface ResolutionWaiting {
  duplicates: number
  /** Unresolved photo matches; null while Advanced Face Detection is off (ADR-0051). */
  faces: number | null
  /** Open tattoo leads; null while HUMINT is off. */
  tattoos: number | null
  /** Open voice leads; null while HUMINT is off. */
  voices: number | null
}

/**
 * The entity-resolution rail badge: pending duplicates, plus the unresolved
 * photo matches while Advanced Face Detection is on, plus the open tattoo
 * leads and the open voice leads while HUMINT is on. With neither, the badge
 * is the duplicates' alone, with no mention of the other tabs.
 */
export function entityResolutionBadge(
  t: TFunction,
  { duplicates, faces, tattoos, voices }: ResolutionWaiting
): RailBadge {
  const parts: (readonly [tab: string, waiting: number | null])[] = [
    ['duplicates', duplicates],
    ['photoMatching', faces],
    ['tattooMatching', tattoos],
    ['voiceMatching', voices],
  ]
  const offered = parts.filter((part): part is [string, number] => part[1] !== null)
  const count = offered.reduce((sum, [, waiting]) => sum + waiting, 0)
  if (offered.length === 1 || count === 0) return { count }
  return {
    count,
    label: offered.map(([tab, waiting]) => `${t(`${TABS}.${tab}`)}: ${waiting}`).join(', '),
  }
}
